<div [formGroup]="formGroup" class="scheduler-form">
  <div class="scheduler-form-row-group">
    <div class="scheduler-form-row">
      <label class="scheduler-form-label">
        {{ 'flow.node.trigger.schedule.event-type.label' | translate }}
        <span class="required">*</span>
      </label>

      <div class="scheduler-form-field">
        <cap-dropdown
          formControlName="event_type"
          [options]="eventTypeOptions"
          [sortAlphabetically]="false"
          [placeholder]="'placeholder.select' | translate"
          (changeSelection)="eventTypeChange($event)" />
      </div>
    </div>
  </div>

  <div class="scheduler-form-row-group">
    <div class="scheduler-form-row">
      <label class="scheduler-form-label">
        {{ 'flow.node.trigger.schedule.start-date.label' | translate }}
        <span class="required">*</span>
      </label>

      <div class="scheduler-form-field">
        <cap-calendar
          formControlName="start_date"
          [showIcon]="true"
          [iconDisplay]="'input'"
          [showButtonBar]="true"
          [readonlyInput]="true"
          [placeholder]="'flow.node.trigger.schedule.start-date.placeholder' | translate" />
      </div>
    </div>

    @if (scheduleEventType === eventType.recurring) {
      <div class="scheduler-form-row">
        <label class="scheduler-form-label">
          {{ 'flow.node.trigger.schedule.end-date.label' | translate }}
        </label>

        <div class="scheduler-form-field">
          <cap-calendar
            formControlName="end_date"
            [showIcon]="true"
            [iconDisplay]="'input'"
            [showButtonBar]="true"
            [minDate]="startDate"
            [readonlyInput]="true"
            [placeholder]="'flow.node.trigger.schedule.end-date.placeholder' | translate" />
        </div>
      </div>
    }

    <div class="scheduler-form-row">
      <label class="scheduler-form-label">
        {{ 'calendar-locale.timezone.label' | translate }}
      </label>

      <div class="scheduler-form-field">
        <cap-dropdown
          formControlName="timezone"
          [options]="timezonesList"
          [sortAlphabetically]="false"
          [filter]="true"
          [virtualScroll]="true"
          [virtualScrollItemSize]="30"
          [resetFilterOnHide]="true"
          [placeholder]="'placeholder.select' | translate" />
      </div>
    </div>
  </div>

  <div class="scheduler-form-row-group">
    <div class="scheduler-form-row">
      <label class="scheduler-form-label">
        {{ 'flow.node.trigger.schedule.start-time.label' | translate }}
        <span class="required">*</span>
      </label>

      <div class="scheduler-form-field">
        <nz-time-picker
          formControlName="start_time"
          nzFormat="HH:mm"
          [nzDefaultOpenValue]="defaultOpenValue"
          [nzMinuteStep]="minuteStep"
          [nzInputReadOnly]="true"
          [nzDisabled]="!startDate"
          [nzPlaceHolder]="'flow.node.trigger.schedule.start-time.placeholder' | translate"
          (ngModelChange)="startTimeChange($event)"
          (nzOpenChange)="timePickerOpen($event, true)" />
      </div>
    </div>

    @if (scheduleEventType === eventType.recurring) {
      <div class="scheduler-form-row">
        <label class="scheduler-form-label">
          {{ 'flow.node.trigger.schedule.end-time.label' | translate }}
        </label>
        <div class="scheduler-form-field">
          <nz-time-picker
            formControlName="end_time"
            nzFormat="HH:mm"
            [nzMinuteStep]="minuteStep"
            [nzDefaultOpenValue]="defaultOpenValue"
            [nzInputReadOnly]="true"
            [nzDisabled]="!startTime"
            [nzDisabledHours]="disabledEndHours.bind(this)"
            [nzPlaceHolder]="'flow.node.trigger.schedule.end-time.placeholder' | translate"
            (nzOpenChange)="timePickerOpen($event)" />
        </div>
      </div>
    }
  </div>

  @if (scheduleEventType === eventType.recurring) {
    <div class="scheduler-form-row-group">
      <div class="scheduler-form-row">
        <label class="scheduler-form-label">
          {{ 'flow.node.trigger.schedule.repeat.label' | translate }}
        </label>

        <div class="scheduler-form-field">
          <cap-dropdown
            formControlName="repeat"
            [options]="repeatOptions"
            [sortAlphabetically]="false"
            [placeholder]="'flow.node.trigger.schedule.repeat.placeholder' | translate"
            (changeSelection)="schedulerRepeatChange($event)" />
        </div>
      </div>

      @if (selectedRepeat !== null) {
        @switch (selectedRepeat) {
          @case (schedulerRepeat.hourly) {
            <div class="scheduler-form-row">
              <label class="scheduler-form-label">
                {{ 'flow.node.trigger.schedule.every.label' | translate }}
              </label>

              <div class="scheduler-form-field">
                <div class="interval-wrapper">
                  <div class="interval-input">
                    <input
                      inputmode="numeric"
                      min="0"
                      max="24"
                      formControlName="hours"
                      [pKeyFilter]="intervalRegExp.hours24"
                      placeholder="0"
                      class="interval-input-field"
                      (focus)="onFocus($event)" />

                    <span>{{
                      'calendar-locale.time.hour.label' | translate: { count: formGroup.get('hours')?.value || 0 }
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          }

          @case (schedulerRepeat.weekly) {
            <div class="scheduler-form-row">
              <label class="scheduler-form-label">
                {{ 'flow.node.trigger.schedule.every.label' | translate }}
              </label>

              <div class="scheduler-form-field">
                <cap-select-button
                  formControlName="days"
                  styleClass="weekly"
                  [options]="dayOptions"
                  [multiple]="true" />
              </div>
            </div>
          }

          @case (schedulerRepeat.monthly) {
            <div class="scheduler-form-row">
              <label class="scheduler-form-label">
                {{ 'flow.node.trigger.schedule.on-the.label' | translate }}
              </label>

              <div class="scheduler-form-field">
                <div class="scheduler-form-field-group">
                  <cap-dropdown
                    formControlName="dayOfMonth"
                    [options]="monthDaysOptions"
                    [sortAlphabetically]="false"
                    [placeholder]="'flow.node.trigger.schedule.repeat.placeholder' | translate" />

                  <span>
                    {{ 'general.day' | translate | lowercase }},
                    {{ 'flow.node.trigger.schedule.of-every.label' | translate | lowercase }}
                  </span>

                  <cap-dropdown
                    formControlName="month"
                    [options]="numberMonthsOptions"
                    [sortAlphabetically]="false"
                    [placeholder]="'flow.node.trigger.schedule.repeat.placeholder' | translate" />

                  <span>{{ 'general.months' | translate }}</span>
                </div>
              </div>
            </div>
          }

          @case (schedulerRepeat.yearly) {
            <div class="scheduler-form-row">
              <label class="scheduler-form-label">
                {{ 'flow.node.trigger.schedule.on-the.label' | translate }}
              </label>

              <div class="scheduler-form-field">
                <div class="scheduler-form-field-group">
                  <cap-dropdown
                    formControlName="dayOfMonth"
                    [options]="monthDaysOptions"
                    [sortAlphabetically]="false"
                    [showClear]="true"
                    [placeholder]="'flow.node.trigger.schedule.repeat.placeholder' | translate" />

                  <span>
                    {{ 'general.day' | translate | lowercase }},
                    {{ 'flow.node.trigger.schedule.of.label' | translate | lowercase }}
                  </span>

                  <cap-dropdown
                    formControlName="month"
                    [options]="monthsOptions"
                    [sortAlphabetically]="false"
                    [showClear]="true"
                    [placeholder]="'flow.node.trigger.schedule.repeat.placeholder' | translate" />

                  <span>{{ 'general.month' | translate }}</span>
                </div>
              </div>
            </div>
          }

          @case (schedulerRepeat.advanced) {
            <div class="scheduler-form-row">
              <label class="scheduler-form-label">
                {{ 'flow.node.trigger.schedule.cron-expression.label' | translate }}
              </label>

              <div class="scheduler-form-field">
                <input
                  formControlName="cron_expression"
                  class="cron-expression-input"
                  placeholder="* * * * * *"
                  (focus)="onFocus($event)" />
              </div>
            </div>
          }
        }

        @if (selectedRepeat !== schedulerRepeat.advanced) {
          <div class="scheduler-form-row">
            <label class="scheduler-form-label">
              {{ 'flow.node.trigger.schedule.at-time.label' | translate }}
            </label>
            <div class="scheduler-form-field">
              <div class="interval-wrapper">
                @if (selectedRepeat !== schedulerRepeat.hourly) {
                  <div class="interval-input">
                    <input
                      inputmode="numeric"
                      min="0"
                      max="23"
                      formControlName="hours"
                      [pKeyFilter]="intervalRegExp.hours"
                      placeholder="0"
                      class="interval-input-field"
                      (focus)="onFocus($event)" />

                    <span>{{ 'calendar-locale.time.hour.label' | translate: { count: 0 } }}</span>
                  </div>
                }

                <div class="interval-input">
                  <input
                    inputmode="numeric"
                    min="0"
                    max="59"
                    formControlName="minutes"
                    [pKeyFilter]="intervalRegExp.minutes"
                    class="interval-input-field"
                    placeholder="0"
                    (focus)="onFocus($event)" />

                  <span>{{ 'calendar-locale.time.minute.label' | translate: { count: 0 } }}</span>
                </div>

                <!-- for now we don't have seconds, will be implemented in the future-->
                @if (false) {
                  <div class="interval-input">
                    <input
                      inputmode="numeric"
                      min="0"
                      max="59"
                      formControlName="seconds"
                      [pKeyFilter]="intervalRegExp.seconds"
                      class="interval-input-field"
                      placeholder="0"
                      (focus)="onFocus($event)" />

                    <span>{{ 'calendar-locale.time.second.label' | translate: { count: 0 } }}</span>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      }
    </div>
  }

  <div class="scheduler-form-row-group">
    <div class="scheduler-form-row">
      <div class="scheduler-form-cron-description">
        <span>{{ cronDescription() }}</span>

        @if (cronNextExecutionDates().length && !isSingleEvent()) {
          <div class="next-execution-dates">
            <h5 class="title">{{ 'flow.node.trigger.schedule.next-execution-dates.label' | translate }}</h5>

            @for (date of cronNextExecutionDates(); track $index) {
              <div class="execution-date">{{ date }}</div>
            }
          </div>
        }
      </div>
    </div>
  </div>
</div>
